<template>
  <transition :name="animation">
    <div
      v-if="isActive"
      ref="loading"
      class="ak-loading-overlay is-active"
      :class="{ 'ak-loading-overlay--full-page': displayInFullPage }"
    >
      <div
        class="ak-loading-overlay__backdrop"
        :class="{ 'ak-loading-overlay__backdrop--bg': backdrop }"
        @click="cancel"
      />
      <div class="ak-loading-overlay__body">
        <slot>
          <ak-loading-spinner :size="size" :color="color" />
        </slot>
      </div>
    </div>
  </transition>
</template>

<script>
import { computed, ref, defineComponent, onMounted } from 'vue'
import { HTMLElement } from '../../utils/ssr'
import LoadingSpinner from '../loading/LoadingSpinner.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-loading :active.sync=true />
   */
  name: 'AkLoading',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [LoadingSpinner.name]: LoadingSpinner,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Boolean}
     * @description Whether modal is active or not, use the .sync
     * modifier to make it two-way binding
     */
    active: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Used internally by the programatic loading
     * plugin found in 'src/components/loading/index.js' it
     * needs to be installed into vue
     * @example
     * import Loading from 'src/components/loading/index.js'
     * import Vue from 'vue
     * Vue.use(Loading)
     */
    programmatic: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Mixed}
     * @description The container of the loader
     */
    container: {
      type: [Object, Function, HTMLElement],
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @description Loader will overlay the full page
     */
    isFullPage: {
      type: Boolean,
      default: true,
    },

    /**
     * @type {String}
     * @description Custom animation (transition name)
     */
    animation: {
      type: String,
      default: 'fade',
    },

    /**
     * @type {Boolean}
     * @description Can close Loading by pressing escape or clicking outside
     */
    canCancel: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Callback function to call after user canceled
     * (pressed escape / clicked outside)
     */
    onCancel: {
      type: Function,
      validator: (v) => typeof v === 'function',
      default: () => {},
    },

    /**
     * @type {String}
     * @description The size of icon to display
     */
    spinnerSize: {
      validator: (v) => ['default', 'small', 'tiny'].indexOf(v) >= 0,
      type: String,
      default: 'default',
    },

    /**
     * @type {String}
     * @description The color of the spinner to display
     */
    spinnerColor: {
      type: String,
      default: 'primary',
    },

    /**
     * @type {Boolean}
     * @description Show background backdrop
     */
    backdrop: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['close', 'update:active', 'update:isFullPage'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const color = computed(() => props.spinnerColor)
    const size = computed(() => props.spinnerSize)
    const loading = ref(null)

    const displayInFullPage = computed({
      get() {
        return props.isFullPage
      },
      set(val) {
        emit('update:isFullPage', val)
      },
    })

    const isActive = computed({
      get() {
        return props.active || false
      },
      set(val) {
        emit('update:active', val)
      },
    })

    onMounted(() => {
      if (props.programmatic) {
        if (!props.container) {
          document.body.appendChild(loading.value)
        } else {
          displayInFullPage.value = false
          emit('update:isFullPage', false)
          props.container.appendChild(loading.value)
        }
      }
    })

    const cancel = (e) => {
      /* istanbul ignore if */
      if (e && /*istanbul ignore next */ e.preventDefault) {
        e.preventDefault()
      }

      if (props.canCancel) {
        props.onCancel()
        return close()
      }
    }

    const close = () => {
      isActive.value = false
      emit('close')
      emit('update:active', false)
    }

    return {
      cancel,
      close,
      displayInFullPage,
      isActive,
      loading,
      color,
      size,
    }
  },
})
</script>

<style lang="scss">
.ak-loading-overlay {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  align-items: center;
  display: none;
  justify-content: center;
  overflow: hidden;
  z-index: 2;

  &--full-page {
    position: fixed;
  }

  &.is-active {
    display: flex;
  }

  &__backdrop {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;

    &--bg {
      background: rgba(255, 255, 255, 0.75);
    }
  }

  &__body {
    position: relative;
    z-index: 1;
  }
}
</style>
